import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { clientGeneratableIdSchema, generateId } from "../id";
import { CHANGE_REVIEW_TIMEOUT_MS } from "../publishedChangeReview";
import { mutator } from "./mutator";
import { requestWorkflowChangeReview } from "./requestWorkflowChangeReview";
import { assertNotGitSynced } from "./syncGit";

export const requestPushLiveSchema = z.object({
  id: clientGeneratableIdSchema,
  branchId: z.string(),
  reviewId: clientGeneratableIdSchema,
  reviewerIds: z.array(z.string()).min(1).optional(),
});

export const requestPushLive = mutator({
  schema: requestPushLiveSchema,
  description:
    "Asks a peer to approve a branch so its author can push it live.",
  interactionType: "review",
  resolve: {
    branch: { kind: "branch", from: "branchId" },
  },
  // An approval only unlocks pushing for someone who can push live themselves.
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
    {
      permission: "space:push_live",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
  ],
  fn: async ({ tx, ctx, args: { id, reviewId, reviewerIds, branch } }) => {
    const { workflow } = branch;
    const space = await tx.run(zql.spaces.where("id", workflow.spaceId).one());
    if (!space?.blockSelfApproval)
      throw userFacingError("This space doesn’t require peer review");
    await assertNotGitSynced(tx, workflow.spaceId);
    if (branch.name === "main" || !branch.headCommitId)
      throw userFacingError("Nothing to push live on this branch");
    const commitId = branch.headCommitId;

    const previous = await tx.run(
      zql.pushLiveRequests.where("branchId", branch.id).one()
    );
    if (previous) await tx.mutate.pushLiveRequests.delete({ id: previous.id });
    await tx.mutate.pushLiveRequests.insert({
      id,
      tenantId: ctx.tenantId,
      branchId: branch.id,
      commitId,
      requestedByUserId: ctx.userId,
      createdAt: Date.now(),
      approvedByUserId: null,
      approvedAt: null,
    });
    for (const userId of new Set(reviewerIds)) {
      await tx.mutate.pushLiveRequestReviewers.insert({
        id: generateId(),
        tenantId: ctx.tenantId,
        requestId: id,
        userId,
      });
    }

    const review = await tx.run(
      zql.workflowChangeReviews
        .where("commitId", commitId)
        .orderBy("createdAt", "desc")
        .orderBy("id", "desc")
        .one()
    );
    const reusable =
      review?.status === "complete" ||
      (review?.status === "generating" &&
        review.createdAt > Date.now() - CHANGE_REVIEW_TIMEOUT_MS);
    if (reusable) return;
    await requestWorkflowChangeReview.fn({
      tx,
      ctx,
      args: { id: reviewId, workflowId: workflow.id, commitId },
    });
  },
});
